import type { SupabaseClient } from '@supabase/supabase-js';

/**
 * The four service boards: haulage jobs, hauliers directory, crops/feed/bedding
 * and machinery hire.
 *
 * These are not member listings. They are entries the FarmTradeDirect team adds
 * by hand in Admin → Boards, and every one carries an enquiry button that sends
 * to the address in site_settings.board_enquiry_email.
 *
 * All four used to be hardcoded arrays in the page files. The hauliers directory
 * in particular listed eight invented companies with invented contacts, ratings
 * and email addresses, which is why nothing here ships with sample data: an empty
 * board is honest, an invented one is not.
 */

export type BoardKey = 'haulage' | 'hauliers' | 'crops-feed-bedding' | 'machinery-hire';

export const BOARDS: { key: BoardKey; label: string; noun: string }[] = [
  { key: 'haulage', label: 'Haulage Jobs', noun: 'job' },
  { key: 'hauliers', label: 'Hauliers Directory', noun: 'haulier' },
  { key: 'crops-feed-bedding', label: 'Grain, Feed & Bedding', noun: 'listing' },
  { key: 'machinery-hire', label: 'Machinery Hire', noun: 'machine' },
];

export interface BoardEntry {
  id: string;
  board: BoardKey;
  title: string;
  subtitle: string | null;
  description: string | null;
  category: string | null;
  location_town: string | null;
  county: string | null;
  coverage: string | null;
  price: number | null;
  price_note: string | null;
  details: Record<string, string>;
  image_url: string | null;
  enquiry_email: string | null;
  is_published: boolean;
  is_featured: boolean;
  /** Set when a member paid to list this rather than an admin adding it. */
  posted_by: string | null;
  is_paid: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

const COLUMNS =
  'id, board, title, subtitle, description, category, location_town, county, coverage, ' +
  'price, price_note, details, image_url, enquiry_email, is_published, is_featured, ' +
  'posted_by, is_paid, sort_order, created_at, updated_at';

/** Published entries for a public board page. */
export async function fetchBoardEntries(
  supabase: SupabaseClient,
  board: BoardKey,
  opts: { category?: string; county?: string; q?: string } = {}
): Promise<BoardEntry[]> {
  let query = supabase
    .from('board_entries')
    .select(COLUMNS)
    .eq('board', board)
    .eq('is_published', true);

  if (opts.category) query = query.eq('category', opts.category);
  if (opts.county) query = query.eq('county', opts.county);
  if (opts.q?.trim()) {
    const term = opts.q.trim().replace(/[,()]/g, ' ');
    query = query.or(
      `title.ilike.%${term}%,subtitle.ilike.%${term}%,description.ilike.%${term}%,category.ilike.%${term}%`
    );
  }

  const { data, error } = await query
    .order('is_featured', { ascending: false })
    .order('sort_order', { ascending: true })
    .order('created_at', { ascending: false });
  if (error) throw error;
  return normalise(data);
}

/** Every entry on a board, drafts included. RLS restricts this to admins. */
export async function fetchAllBoardEntries(
  supabase: SupabaseClient,
  board: BoardKey
): Promise<BoardEntry[]> {
  const { data, error } = await supabase
    .from('board_entries')
    .select(COLUMNS)
    .eq('board', board)
    .order('sort_order', { ascending: true })
    .order('created_at', { ascending: false });
  if (error) throw error;
  return normalise(data);
}

function normalise(rows: unknown): BoardEntry[] {
  return ((rows ?? []) as BoardEntry[]).map((e) => ({
    ...e,
    details: e.details && typeof e.details === 'object' ? e.details : {},
  }));
}

export async function createBoardEntry(
  supabase: SupabaseClient,
  entry: Partial<BoardEntry> & { board: BoardKey; title: string }
): Promise<BoardEntry> {
  const { data, error } = await supabase
    .from('board_entries')
    .insert(entry)
    .select(COLUMNS)
    .single();
  if (error) throw error;
  return normalise([data])[0];
}

export async function saveBoardEntry(
  supabase: SupabaseClient,
  id: string,
  patch: Partial<BoardEntry>
): Promise<void> {
  const { error } = await supabase.from('board_entries').update(patch).eq('id', id);
  if (error) throw error;
}

export async function deleteBoardEntry(
  supabase: SupabaseClient,
  id: string
): Promise<void> {
  const { error } = await supabase.from('board_entries').delete().eq('id', id);
  if (error) throw error;
}

/** The distinct categories actually in use on a board, for the filter chips. */
export function categoriesOf(entries: BoardEntry[]): string[] {
  return Array.from(
    new Set(entries.map((e) => e.category).filter((c): c is string => Boolean(c)))
  ).sort();
}

export function countiesOf(entries: BoardEntry[]): string[] {
  return Array.from(
    new Set(entries.map((e) => e.county).filter((c): c is string => Boolean(c)))
  ).sort();
}

/** "£450 per day" / "POA". */
export function priceLabel(entry: BoardEntry): string {
  if (entry.price == null) return entry.price_note?.trim() || 'POA';
  const amount = `£${Number(entry.price).toLocaleString('en-GB', {
    minimumFractionDigits: Number(entry.price) % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  })}`;
  return entry.price_note?.trim() ? `${amount} ${entry.price_note.trim()}` : amount;
}

export function whereLabel(entry: BoardEntry): string {
  return [entry.location_town, entry.county].filter(Boolean).join(', ');
}
